import React, { useState, useLayoutEffect } from "react";
import { Card, Col, Nav, Row, Button } from "react-bootstrap";
import { auctionCampaignStatusCardLabels, buttonLabel, advDashboard } from "@revbox/localization";
import { cookiesKeyNames } from "@revbox/common-fe";
import { getClientCampaings } from "../services/adv";
import { useNavigate, Link } from "react-router-dom";
import { useCookiesContext } from "../contexts/CookiesContext";
import { useJoyride } from "../contexts/JoyrideContext";
import { OverlayContainer } from "../services/currency/RenderTooltip";
import { useEurUsdRate } from "../hooks/useEurUsdRate";
import { addPubParamToUrl } from "../helpers/add-pub-param-to-url";
import ApvSurveyBanner from "./campaign/components/common/ApvSurveyBanner";

const APV_PUBLISHER_ID = 73;

const nullDayFix = (nextInvoiceDate) => /null|0/.test(nextInvoiceDate) ?
nextInvoiceDate.replace(/null/, '1') :
nextInvoiceDate

export default function Home() {
  const [data, setData] = useState([]);
  const { getAdvertiserId, getLanguageAbbr, getPublisherId } = useCookiesContext();
  const language = getLanguageAbbr();
  const labelItems = advDashboard[language].items;
  const statusCardLabels = auctionCampaignStatusCardLabels[language].status;
  const { rate } = useEurUsdRate();
  const advIdByCookies = getAdvertiserId();
  const { startTour, isTourTriggered } = useJoyride();
  const { getIsTourCompleted } = useCookiesContext();
  const urlParams = new URLSearchParams(window.location.search);
  const refresh = urlParams.get("refresh");
  const pubValue = urlParams.get("pub");
  const pubValueFromCookies = getPublisherId();
  const navigate = useNavigate();

  const statusClassMap = {
    active: "text-success",
    inactive: "text-danger",
    paused: "text-warning"
  }

  useLayoutEffect(() => {
    if (!advIdByCookies) return;
    getClientCampaings(advIdByCookies)
      .then((res) => {
        setData(res.data);
      })
      .catch((e) => {
        console.log("Server error", e);
      })
      .finally(() => {
        setTimeout(() => {
          if (data.length === 0) return;
          if ([67, 10, "67", "10"].includes(pubValue)) return;
        }, 200)
        const tourKey = cookiesKeyNames.tourAdvInit
        const isTourCompleted = getIsTourCompleted(tourKey)
        if (isTourCompleted === false || isTourTriggered) {
          startTour(tourKey);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advIdByCookies, isTourTriggered]);

  if (refresh) {
    window.location.href = "/adv/home";
  }

  return (
    <div className="main main-app p-3 p-lg-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item">
              <Link href="/adv/home" to={addPubParamToUrl("/adv/home", pubValue, advIdByCookies)}>
                {advDashboard[language].home}
              </Link>
            </li>
          </ol>
          <h2 className="main-title mb-0">{advDashboard[language].breadcrumb}</h2>
        </div>
      </div>
      <Row className="g-3" id="adv-dashboard-card">
        {Number(pubValueFromCookies) === APV_PUBLISHER_ID && <ApvSurveyBanner language={language} />}
        {data &&
          data.map((campaign, index) => (
            <Col
              xl="12"
              key={index}
              onClick={() =>
                navigate(addPubParamToUrl(`/adv/campaign/product?campaignId=${campaign.id}`, pubValue, advIdByCookies))
              }
            >
              <Card className="card-one cursor-pointer">
                <Card.Header>
                  <Card.Title as="h6">{campaign.name}</Card.Title>
                  <Nav className="nav-icon nav-icon-sm ms-auto">
                    <Nav.Link href="">
                      <i className="ri-edit-line"></i>
                    </Nav.Link>
                  </Nav>
                </Card.Header>
                <Card.Body>
                  <div className="p-2">
                    <Row className="g-3">
                      <Col sm="3">
                        {campaign.status && (
                          <h3 className={`card-value fw-bold mb-1 ${statusClassMap[campaign.status]}`}>
                            {statusCardLabels[campaign.status]}
                          </h3>
                        )}
                        {/* <h3 className="card-value fw-bold mb-1 text-success">{campaign.status.toUpperCase()}</h3> */}
                        <label className="card-title fw-semibold text-dark mb-1">
                          {labelItems.campaign_status.title}
                        </label>
                        <p className="mb-0 fs-xs text-secondary">
                          {labelItems.campaign_status.description}
                        </p>
                      </Col>
                      <Col sm="3">
                        <OverlayContainer amount={campaign.total_spend} rate={rate}>
                          <h3 className="card-value mb-1" style={{ width: 'max-content' }}>
                            <span>$</span>
                            {campaign.total_spend}
                          </h3>
                        </OverlayContainer>
                        <label className="card-title fw-semibold text-dark mb-1">
                          {labelItems.spend_since_last_invoice.title}
                        </label>
                        <p className="mb-0 fs-xs text-secondary">
                          {labelItems.spend_since_last_invoice.description}
                        </p>
                      </Col>
                      <Col sm="3">
                        <h3 className="card-value mb-1">
                          {campaign.total_clicks}
                        </h3>
                        <label className="card-title fw-semibold text-dark mb-1">
                          {labelItems.clicks_since_last_invoice.title}
                        </label>
                        <p className="mb-0 fs-xs text-secondary">
                          {labelItems.clicks_since_last_invoice.description}
                        </p>
                      </Col>
                      <Col sm="3">
                        <h3 className="card-value mb-1">
                          {nullDayFix(campaign.next_invoice_date)}
                        </h3>
                        <label className="card-title fw-semibold text-dark mb-1">
                          {labelItems.next_billing_date.title}
                        </label>
                        <p className="mb-0 fs-xs text-secondary">
                          {labelItems.next_billing_date.description}
                        </p>
                      </Col>
                      <Col sm="3" className="d-none d-sm-block"></Col>
                      <Col sm="3">
                        <OverlayContainer amount={campaign.spend_credits} rate={rate}>
                          <h3 className="card-value mb-1" style={{ width: 'max-content' }}>
                            <span>$</span>
                            {campaign.spend_credits}
                          </h3>
                        </OverlayContainer>
                        <label className="card-title fw-semibold text-dark mb-1">
                          {labelItems.credits.title}
                        </label>
                        <p className="mb-0 fs-xs text-secondary">
                          {labelItems.credits.description}
                        </p>
                      </Col>
                      <Col sm="3">
                        <h3 className="card-value mb-1">
                          {campaign.total_impressions}
                        </h3>
                        <label className="card-title fw-semibold text-dark mb-1">
                          {labelItems.impressions_since_last_invoice.title}
                        </label>
                        <p className="mb-0 fs-xs text-secondary">
                          {labelItems.impressions_since_last_invoice.description}
                        </p>
                      </Col>
                      <Col sm="3">
                        <h3 className="card-value mb-1">
                          {campaign.total_active_bids}
                        </h3>
                        <label className="card-title fw-semibold text-dark mb-1">
                          {labelItems.live_inventory_bids.title}
                        </label>
                        <p className="mb-0 fs-xs text-secondary">
                          {labelItems.live_inventory_bids.description}
                        </p>
                      </Col>
                    </Row>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        <Col xl="12">
          <Button
            href="/adv/campaign/new"
            className=""
            variant="outline-primary"
            disabled
          >
            <i className="ri-add-line"></i> {buttonLabel[language].newCampaign}
          </Button>
        </Col>
      </Row>
    </div>
  );
}
